// Staff-side inbox for client-submitted portal_requests. Triage flow:
//   new → in_review → converted (job created)
//                  → declined  (with optional reason)
//
// RLS: workspace_isolation gives staff full read/write across their
// workspace. No special policies needed — the portal-write side is
// already locked down at write time, and once a request is in the
// system staff own it.
//
// "Convert to job" hands off to NewJobModal with a Defaults payload
// pulled from the request (title, contact, company, location,
// priority, description → job_notes). On successful job create the
// inbox stamps converted_job_id + converted_at + converted_by + flips
// status to 'converted'. We use AppLayout's existing nav-badge polling
// (30s) to refresh the "new" counter elsewhere.

import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { formatRelativeDate } from '@/lib/utils'
import {
  ClipboardList, Inbox, CheckCircle2, XCircle, Wrench, Loader2,
  AlertCircle, MapPin, User as UserIcon, Building2, X, Plus, MessageSquare, Play, Check,
} from 'lucide-react'
import { toast } from 'sonner'
import NewJobModal from '@/components/NewJobModal'
import LogRequestModal from '@/components/LogRequestModal'
import { notifyPortal } from '@/lib/portalNotify'

interface RequestRow {
  id: string
  subject: string
  description: string | null
  status: 'new' | 'in_review' | 'converted' | 'declined' | string
  priority: 'normal' | 'urgent' | string
  workspace_id: string
  company_id: string | null
  contact_id: string | null
  contact_name: string
  contact_email: string | null
  location_id: string | null
  converted_job_id: string | null
  converted_at: string | null
  declined_reason: string | null
  created_at: string
  conversation_id: string | null
  assigned_to: string | null
  source: string
  resolution_type: string | null
  resolution_note: string | null
  resolved_at: string | null
  // Computed via select() expansions below
  company?: { id: string; name: string } | null
  location?: { id: string; name: string } | null
}

const STATUSES = [
  { id: 'new',        label: 'New',        color: 'var(--info)',       bg: 'var(--info-dim)',     border: 'var(--info-border)' },
  { id: 'in_review',  label: 'In review',  color: 'var(--accent-text)', bg: 'var(--accent-dim)',   border: 'var(--accent-border)' },
  { id: 'in_progress', label: 'In progress', color: 'var(--warning)',   bg: 'var(--warning-dim)',  border: 'var(--warning-border)' },
  { id: 'converted',  label: 'Converted',  color: 'var(--positive)',   bg: 'var(--positive-dim)', border: 'var(--positive-border)' },
  { id: 'resolved',   label: 'Resolved',   color: 'var(--positive)',   bg: 'var(--positive-dim)', border: 'var(--positive-border)' },
  { id: 'declined',   label: 'Declined',   color: 'var(--danger)',     bg: 'var(--danger-dim)',   border: 'var(--danger-border)' },
] as const

type FilterId = (typeof STATUSES)[number]['id']

const SOURCE_META: Record<string, string> = { portal: 'Portal', internal: 'Internal', email: 'Email' }
const RESOLUTION_LABELS: Record<string, string> = {
  job_dispatched: 'Job dispatched', callback: 'Callback', credit_issued: 'Credit issued', no_action: 'No action', other: 'Other',
}
// Options offered in ResolveDialog — job_dispatched is auto-stamped by convert, not manual.
const RESOLUTION_OPTIONS = ['callback', 'credit_issued', 'no_action', 'other'] as const

export default function RequestsInbox() {
  const { workspace, profile } = useAuth()
  const queryClient = useQueryClient()
  const [activeFilters, setActiveFilters] = useState<Set<FilterId>>(new Set(['new', 'in_review', 'in_progress']))
  const [assignedToMe, setAssignedToMe] = useState(false)
  const [logOpen, setLogOpen] = useState(false)
  const [convertTarget, setConvertTarget] = useState<RequestRow | null>(null)
  const [declineTarget, setDeclineTarget] = useState<RequestRow | null>(null)
  const [resolveTarget, setResolveTarget] = useState<RequestRow | null>(null)
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['requests-inbox', workspace?.id] })
    queryClient.invalidateQueries({ queryKey: ['portal-requests-new-count', workspace?.id] })
  }

  const { data: requests = [], isLoading } = useQuery({
    queryKey: ['requests-inbox', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      // Inline expansions: company + location names for display. RLS
      // workspace_isolation scopes both — no extra filter needed.
      const { data, error } = await supabase
        .from('portal_requests')
        .select(`
          id, subject, description, status, priority,
          workspace_id, company_id, contact_id, contact_name, contact_email,
          location_id, converted_job_id, converted_at, declined_reason, created_at, conversation_id,
          assigned_to, source, resolution_type, resolution_note, resolved_at,
          company:companies(id, name),
          location:locations(id, name)
        `)
        .eq('workspace_id', workspace.id)
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as unknown as RequestRow[]
    },
    enabled: !!workspace?.id,
  })

  // Workspace members for the assignee picker + name resolution (portal_requests
  // stores only assigned_to; names resolve here). Mirrors ConversationDetail.
  const { data: agents = [] } = useQuery<Array<{ id: string; full_name: string | null }>>({
    queryKey: ['agents', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('profiles').select('id, full_name, role').eq('workspace_id', workspace.id)
      return (data ?? []) as Array<{ id: string; full_name: string | null }>
    },
    enabled: !!workspace?.id,
  })
  const agentName = useMemo(() => new Map(agents.map(a => [a.id, a.full_name ?? 'Unknown'])), [agents])

  const filtered = useMemo(() => {
    let rows = activeFilters.size === 0 ? requests : requests.filter(r => activeFilters.has(r.status as FilterId))
    if (assignedToMe && profile?.id) rows = rows.filter(r => r.assigned_to === profile.id)
    return rows
  }, [requests, activeFilters, assignedToMe, profile?.id])

  const counts = useMemo(() => {
    const c: Record<FilterId, number> = { new: 0, in_review: 0, in_progress: 0, converted: 0, resolved: 0, declined: 0 }
    for (const r of requests) {
      if (r.status in c) c[r.status as FilterId]++
    }
    return c
  }, [requests])

  const toggleFilter = (id: FilterId) => {
    setActiveFilters(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const startMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('portal_requests').update({ status: 'in_progress' }).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => { toast.success('Started'); invalidate() },
    onError: (e: Error) => toast.error(`Failed: ${e.message}`),
  })

  // Assign a request to a member (or unassign). Bells the new assignee
  // (request_assigned type — added by 20260711201205) unless it's a self-assign.
  const assignMutation = useMutation({
    mutationFn: async ({ req, agentId }: { req: RequestRow; agentId: string | null }) => {
      const { error } = await supabase.from('portal_requests').update({ assigned_to: agentId }).eq('id', req.id)
      if (error) throw error
      if (agentId && agentId !== profile?.id) {
        await supabase.from('notifications').insert({
          workspace_id: req.workspace_id,
          target_user_id: agentId,
          type: 'request_assigned',
          title: 'Request assigned to you',
          body: req.subject,
          link: '/app/requests',
        })
      }
    },
    onSuccess: (_d, v) => { toast.success(v.agentId ? 'Assigned' : 'Unassigned'); invalidate() },
    onError: (e: Error) => toast.error(`Failed: ${e.message}`),
  })

  return (
    <div style={{ padding: '20px 28px', maxWidth: 1100, width: '100%', margin: '0 auto' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
        <ClipboardList style={{ width: 20, height: 20, color: 'var(--ink-secondary)' }} />
        <h1 style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', margin: 0, letterSpacing: '-0.02em' }}>Requests</h1>
        <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0 }}>Client-submitted and staff-raised requests.</p>
        <button onClick={() => setLogOpen(true)} className="btn-primary" style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Plus style={{ width: 15, height: 15 }} /> Log request
        </button>
      </header>

      <LogRequestModal open={logOpen} onClose={() => setLogOpen(false)}
        onSaved={() => { setLogOpen(false); queryClient.invalidateQueries({ queryKey: ['requests-inbox', workspace?.id] }) }}
        workspaceId={workspace?.id ?? null} />


      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
        {STATUSES.map(s => {
          const active = activeFilters.has(s.id)
          return (
            <button key={s.id} type="button" onClick={() => toggleFilter(s.id)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '6px 12px', borderRadius: 100,
                fontSize: 12, fontWeight: 600, cursor: 'pointer',
                background: active ? s.bg : 'transparent',
                color: active ? s.color : 'var(--ink-tertiary)',
                border: `1px solid ${active ? s.border : 'var(--border-subtle)'}`,
              }}>
              {s.label}
              <span style={{ fontSize: 10, opacity: 0.75, fontVariantNumeric: 'tabular-nums' }}>{counts[s.id]}</span>
            </button>
          )
        })}
        <button type="button" onClick={() => setAssignedToMe(v => !v)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 100, fontSize: 12, fontWeight: 600, cursor: 'pointer', marginLeft: 4,
            background: assignedToMe ? 'var(--accent-dim)' : 'transparent', color: assignedToMe ? 'var(--accent-text)' : 'var(--ink-tertiary)', border: `1px solid ${assignedToMe ? 'var(--accent-border)' : 'var(--border-subtle)'}` }}>
          <UserIcon style={{ width: 11, height: 11 }} /> Assigned to me
        </button>
      </div>

      {isLoading ? (
        <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', textAlign: 'center', padding: '32px 0' }}>Loading…</p>
      ) : filtered.length === 0 ? (
        <div style={{ padding: '40px 16px', textAlign: 'center' }}>
          <Inbox style={{ width: 32, height: 32, color: 'var(--ink-tertiary)', margin: '0 auto 12px', display: 'block' }} />
          <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-secondary)', margin: '0 0 4px' }}>Inbox empty</p>
          <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0 }}>
            {activeFilters.size === 0 ? 'Select a status filter to see requests.' : 'Nothing matches the active filters.'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map(r => (
            <RequestRowView key={r.id} req={r}
              agents={agents} agentName={agentName} myId={profile?.id ?? null}
              onStart={() => startMutation.mutate(r.id)}
              onConvert={() => setConvertTarget(r)}
              onResolve={() => setResolveTarget(r)}
              onDecline={() => setDeclineTarget(r)}
              onAssign={(agentId) => assignMutation.mutate({ req: r, agentId })} />
          ))}
        </div>
      )}

      {convertTarget && (
        <ConvertToJobFlow request={convertTarget} onClose={() => setConvertTarget(null)}
          onConverted={() => { setConvertTarget(null); invalidate() }} />
      )}

      {declineTarget && (
        <DeclineDialog request={declineTarget} onClose={() => setDeclineTarget(null)}
          onDone={() => { setDeclineTarget(null); invalidate() }} />
      )}

      {resolveTarget && (
        <ResolveDialog request={resolveTarget} onClose={() => setResolveTarget(null)}
          onDone={() => { setResolveTarget(null); invalidate() }} />
      )}
    </div>
  )
}

const actBtn = (bg: string, color: string, border = 'none'): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 12px', background: bg, border: border === 'none' ? 'none' : `1px solid ${border}`, borderRadius: 7, color, fontSize: 11, fontWeight: 600, cursor: 'pointer',
})

function RequestRowView({ req, agents, agentName, myId, onStart, onConvert, onResolve, onDecline, onAssign }: {
  req: RequestRow
  agents: Array<{ id: string; full_name: string | null }>
  agentName: Map<string, string>
  myId: string | null
  onStart: () => void
  onConvert: () => void
  onResolve: () => void
  onDecline: () => void
  onAssign: (agentId: string | null) => void
}) {
  const navigate = useNavigate()
  const sCfg = STATUSES.find(s => s.id === req.status) ?? STATUSES[0]
  const isActive = req.status === 'new' || req.status === 'in_review'
  const isProgress = req.status === 'in_progress'
  const isTerminal = req.status === 'converted' || req.status === 'resolved' || req.status === 'declined'

  return (
    <article style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderLeft: `3px solid ${sCfg.color}`, borderRadius: 12, padding: '14px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 100, background: sCfg.bg, color: sCfg.color, border: `1px solid ${sCfg.border}` }}>{sCfg.label}</span>
        <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 100, background: 'var(--surface-sunken)', color: 'var(--ink-tertiary)' }}>{SOURCE_META[req.source] ?? req.source}</span>
        {req.priority === 'urgent' && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 100, background: 'var(--warning-dim)', color: 'var(--warning)', border: '1px solid var(--warning-border)' }}>
            <AlertCircle style={{ width: 10, height: 10 }} /> Urgent
          </span>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          {!isTerminal && <AssigneeControl assignedTo={req.assigned_to} agents={agents} agentName={agentName} onAssign={onAssign} />}
          <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>{formatRelativeDate(req.created_at)}</span>
        </div>
      </div>

      <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)', margin: '0 0 6px' }}>{req.subject}</p>
      {req.description && (
        <p style={{ fontSize: 13, color: 'var(--ink-secondary)', margin: '0 0 8px', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{req.description}</p>
      )}

      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 11, color: 'var(--ink-tertiary)', marginBottom: 12 }}>
        {req.company?.name && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Building2 style={{ width: 11, height: 11 }} /> {req.company.name}</span>}
        {req.contact_name && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><UserIcon style={{ width: 11, height: 11 }} /> {req.contact_name}</span>}
        {req.location?.name && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><MapPin style={{ width: 11, height: 11 }} /> {req.location.name}</span>}
        {req.assigned_to && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><UserIcon style={{ width: 11, height: 11 }} /> {agentName.get(req.assigned_to) ?? 'Assigned'}</span>}
      </div>

      {/* Actions — per-status matrix */}
      {isActive && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button type="button" onClick={onStart} style={actBtn('var(--hover-bg)', 'var(--ink-secondary)', 'var(--border-default)')}><Play style={{ width: 11, height: 11 }} /> Start</button>
          <button type="button" onClick={onConvert} style={actBtn('var(--positive)', 'var(--accent-ink)')}><Wrench style={{ width: 11, height: 11 }} /> Convert to job</button>
          <button type="button" onClick={onDecline} style={actBtn('var(--hover-bg)', 'var(--ink-secondary)', 'var(--border-default)')}><XCircle style={{ width: 11, height: 11 }} /> Decline</button>
        </div>
      )}
      {isProgress && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button type="button" onClick={onConvert} style={actBtn('var(--positive)', 'var(--accent-ink)')}><Wrench style={{ width: 11, height: 11 }} /> Convert to job</button>
          <button type="button" onClick={onResolve} style={actBtn('var(--hover-bg)', 'var(--ink-secondary)', 'var(--border-default)')}><CheckCircle2 style={{ width: 11, height: 11 }} /> Resolve</button>
          <button type="button" onClick={onDecline} style={actBtn('var(--hover-bg)', 'var(--ink-secondary)', 'var(--border-default)')}><XCircle style={{ width: 11, height: 11 }} /> Decline</button>
        </div>
      )}

      {/* Terminal outcome — one visual language for converted / resolved / declined */}
      {isTerminal && (
        <div style={{ background: 'var(--surface-sunken)', border: '1px solid var(--border-subtle)', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: 'var(--ink-secondary)' }}>
          {req.status === 'converted' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 700, color: 'var(--positive)' }}>Job dispatched</span>
              {req.converted_job_id && (
                <button type="button" onClick={() => navigate(`/app/jobs/${req.converted_job_id}`)} style={actBtn('transparent', 'var(--positive)', 'var(--positive-border)')}><CheckCircle2 style={{ width: 11, height: 11 }} /> Open job</button>
              )}
            </div>
          )}
          {req.status === 'resolved' && (
            <div>
              <span style={{ fontWeight: 700, color: 'var(--ink-primary)' }}>Resolved · {RESOLUTION_LABELS[req.resolution_type ?? ''] ?? req.resolution_type ?? '—'}</span>
              {req.resolution_note && <p style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap' }}>{req.resolution_note}</p>}
            </div>
          )}
          {req.status === 'declined' && (
            <div>
              <span style={{ fontWeight: 700, color: 'var(--danger)' }}>Declined</span>
              {req.declined_reason && <p style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap' }}>{req.declined_reason}</p>}
            </div>
          )}
        </div>
      )}

      {req.conversation_id && (
        <button type="button" onClick={() => navigate(`/app/inbox/${req.conversation_id}`)} style={{ ...actBtn('transparent', 'var(--ink-secondary)', 'var(--border-default)'), marginTop: 6 }}>
          <MessageSquare style={{ width: 11, height: 11 }} /> View conversation
        </button>
      )}
    </article>
  )
}

// Assignee dropdown — mirrors ConversationDetail's Assign menu (agents = profiles).
function AssigneeControl({ assignedTo, agents, agentName, onAssign }: {
  assignedTo: string | null
  agents: Array<{ id: string; full_name: string | null }>
  agentName: Map<string, string>
  onAssign: (agentId: string | null) => void
}) {
  const [open, setOpen] = useState(false)
  return (
    <div style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 100, cursor: 'pointer', fontSize: 11, fontWeight: 600, color: assignedTo ? 'var(--accent-text)' : 'var(--ink-tertiary)' }}>
        <UserIcon style={{ width: 11, height: 11 }} />
        {assignedTo ? (agentName.get(assignedTo) ?? 'Assigned').split(' ')[0] : 'Assign'}
      </button>
      {open && (
        <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4, background: 'var(--surface-raised)', border: '1px solid var(--border-default)', borderRadius: 10, overflow: 'hidden', zIndex: 50, minWidth: 150, maxHeight: 240, overflowY: 'auto', boxShadow: 'var(--shadow-popover-mid)' }}>
          {assignedTo && (
            <button type="button" onClick={() => { onAssign(null); setOpen(false) }} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--ink-tertiary)', textAlign: 'left' }}>
              <X style={{ width: 12, height: 12 }} /> Unassign
            </button>
          )}
          {agents.map(a => (
            <button key={a.id} type="button" onClick={() => { onAssign(a.id); setOpen(false) }} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '8px 12px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--ink-secondary)', textAlign: 'left' }}>
              {a.full_name ?? 'Unnamed'}
              {assignedTo === a.id && <Check style={{ width: 12, height: 12, color: 'var(--accent-text)' }} />}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function ConvertToJobFlow({ request, onClose, onConverted }: {
  request: RequestRow
  onClose: () => void
  onConverted: () => void
}) {
  const { workspace, profile } = useAuth()
  const queryClient = useQueryClient()

  // Loader queries for NewJobModal — same shape as FieldHub.
  const enabled = !!workspace?.id
  const { data: contacts = [] } = useQuery({
    queryKey: ['requests-modal-contacts', workspace?.id], enabled,
    queryFn: async () => (await supabase.from('contacts').select('id, first_name, last_name, email, phone, company_name, company_id').eq('workspace_id', workspace!.id).order('first_name')).data ?? [],
  })
  const { data: clients = [] } = useQuery({
    queryKey: ['requests-modal-clients', workspace?.id], enabled,
    queryFn: async () => (await supabase.from('companies').select('id, name').eq('workspace_id', workspace!.id).order('name')).data ?? [],
  })
  const { data: locations = [] } = useQuery({
    queryKey: ['requests-modal-locations', workspace?.id], enabled,
    queryFn: async () => (await supabase.from('locations').select('id, company_id, name, store_number, address_line1, city, state').eq('workspace_id', workspace!.id).order('name')).data ?? [],
  })
  const { data: agents = [] } = useQuery({
    queryKey: ['requests-modal-agents', workspace?.id], enabled,
    queryFn: async () => (await supabase.from('profiles').select('id, full_name, role').eq('workspace_id', workspace!.id)).data ?? [],
  })
  const { data: vendors = [] } = useQuery({
    queryKey: ['requests-modal-vendors', workspace?.id], enabled,
    queryFn: async () => (await supabase.from('vendors').select('id, name, status').eq('workspace_id', workspace!.id).order('name')).data ?? [],
  })
  const { data: serviceLines = [] } = useQuery({
    queryKey: ['requests-modal-service-lines', workspace?.id], enabled,
    queryFn: async () => (await supabase.from('service_lines').select('id, name, is_active').eq('workspace_id', workspace!.id).eq('is_active', true).order('name')).data ?? [],
  })

  const defaults = useMemo(() => {
    const companyName = request.company?.name ?? ''
    return {
      title: request.subject,
      contact_id: request.contact_id ?? undefined,
      contact_name: request.contact_name,
      company_name: companyName,
      company_id: request.company_id ?? undefined,
      job_notes: request.description ?? undefined,
      location_id: request.location_id ?? undefined,
      priority: request.priority,
    }
  }, [request])

  // When the modal calls onSaved (job was created), stamp the request.
  const stamp = useMutation({
    mutationFn: async (jobId: string) => {
      if (!profile?.id) throw new Error('No profile')
      const nowIso = new Date().toISOString()
      const { error } = await supabase.from('portal_requests').update({
        status: 'converted',
        converted_job_id: jobId,
        converted_at: nowIso,
        converted_by: profile.id,
        // Keep the resolution model coherent from birth: converting IS a
        // resolution (job dispatched). CP3 will surface these fields in the UI.
        resolution_type: 'job_dispatched',
        resolved_at: nowIso,
        resolved_by: profile.id,
      }).eq('id', request.id)
      if (error) throw error
    },
    onSuccess: () => {
      toast.success('Request converted to job')
      queryClient.invalidateQueries({ queryKey: ['requests-inbox', workspace?.id] })
      // Best-effort portal notify; the conversion itself is already
      // committed. Helper handles its own toast/silent semantics.
      notifyPortal({ event: 'request_converted', request_id: request.id })
      onConverted()
    },
    onError: (e: Error) => toast.error(`Stamp failed: ${e.message}`),
  })

  return (
    <NewJobModal
      onClose={onClose}
      onSaved={(saved) => {
        if (saved?.id) stamp.mutate(saved.id)
        else onClose()
      }}
      contacts={contacts}
      agents={agents}
      clients={clients}
      locations={locations}
      vendors={vendors}
      serviceLines={serviceLines}
      defaults={defaults} />
  )
}

function DeclineDialog({ request, onClose, onDone }: {
  request: RequestRow
  onClose: () => void
  onDone: () => void
}) {
  const [reason, setReason] = useState('')
  const declineMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from('portal_requests').update({
        status: 'declined',
        declined_reason: reason.trim() || null,
      }).eq('id', request.id)
      if (error) throw error
    },
    onSuccess: () => { toast.success('Request declined'); onDone() },
    onError: (e: Error) => toast.error(`Failed: ${e.message}`),
  })

  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 24 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 16, padding: 22, width: '100%', maxWidth: 460 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <h2 style={{ fontSize: 15, fontWeight: 800, color: 'var(--ink-primary)', margin: 0 }}>Decline request</h2>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0 }}>
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>
        <p style={{ fontSize: 12, color: 'var(--ink-secondary)', margin: '0 0 12px' }}>
          The client will see this reason on their portal.
        </p>
        <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3} autoFocus
          placeholder="Optional reason (e.g. site outside service area)"
          style={{ width: '100%', padding: '9px 11px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 8, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box', resize: 'none', lineHeight: 1.5, marginBottom: 12 }} />
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onClose} disabled={declineMutation.isPending}
            style={{ flex: 1, padding: '9px', background: 'var(--hover-bg)', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--ink-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            Cancel
          </button>
          <button onClick={() => declineMutation.mutate()} disabled={declineMutation.isPending}
            style={{ flex: 1, padding: '9px', background: 'var(--danger)', border: 'none', borderRadius: 8, color: 'var(--accent-ink)', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            {declineMutation.isPending && <Loader2 style={{ width: 13, height: 13, animation: 'spin 1s linear infinite' }} />}
            {declineMutation.isPending ? 'Declining…' : 'Confirm decline'}
          </button>
        </div>
      </div>
    </div>
  )
}

// Resolve a request WITHOUT a job (job_dispatched is auto-stamped by convert).
function ResolveDialog({ request, onClose, onDone }: {
  request: RequestRow
  onClose: () => void
  onDone: () => void
}) {
  const { profile } = useAuth()
  const [rType, setRType] = useState<string>('callback')
  const [note, setNote] = useState('')
  const noteRequired = rType === 'other'
  const canSave = !noteRequired || !!note.trim()

  const resolveMutation = useMutation({
    mutationFn: async () => {
      if (noteRequired && !note.trim()) throw new Error('A note is required for “Other”')
      const { error } = await supabase.from('portal_requests').update({
        status: 'resolved',
        resolution_type: rType,
        resolution_note: note.trim() || null,
        resolved_at: new Date().toISOString(),
        resolved_by: profile?.id ?? null,
      }).eq('id', request.id)
      if (error) throw error
    },
    onSuccess: () => { toast.success('Request resolved'); onDone() },
    onError: (e: Error) => toast.error(`Failed: ${e.message}`),
  })

  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 24 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 16, padding: 22, width: '100%', maxWidth: 460 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <h2 style={{ fontSize: 15, fontWeight: 800, color: 'var(--ink-primary)', margin: 0 }}>Resolve request</h2>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0 }}><X style={{ width: 16, height: 16 }} /></button>
        </div>
        <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 5 }}>Resolution</label>
        <select value={rType} onChange={e => setRType(e.target.value)}
          style={{ width: '100%', padding: '9px 11px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 8, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box', marginBottom: 12, cursor: 'pointer' }}>
          {RESOLUTION_OPTIONS.map(o => <option key={o} value={o}>{RESOLUTION_LABELS[o]}</option>)}
        </select>
        <textarea value={note} onChange={e => setNote(e.target.value)} rows={3}
          placeholder={noteRequired ? 'Required — describe the resolution' : 'Optional note'}
          style={{ width: '100%', padding: '9px 11px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 8, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box', resize: 'none', lineHeight: 1.5, marginBottom: 12 }} />
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onClose} disabled={resolveMutation.isPending}
            style={{ flex: 1, padding: '9px', background: 'var(--hover-bg)', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--ink-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
          <button onClick={() => resolveMutation.mutate()} disabled={resolveMutation.isPending || !canSave}
            style={{ flex: 1, padding: '9px', background: 'var(--accent)', border: 'none', borderRadius: 8, color: 'var(--accent-ink)', fontSize: 13, fontWeight: 700, cursor: canSave ? 'pointer' : 'not-allowed', opacity: canSave ? 1 : 0.5, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            {resolveMutation.isPending && <Loader2 style={{ width: 13, height: 13, animation: 'spin 1s linear infinite' }} />}
            {resolveMutation.isPending ? 'Resolving…' : 'Mark resolved'}
          </button>
        </div>
      </div>
    </div>
  )
}

